import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import * as LucideIcons from "lucide-react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, CircleAlert, CircleCheck, Info, Loader, Trash2 } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { ObjectListTableResizable } from "./object-list-table-resizable-2";
import type { ObjectListColumn } from "./types2";

// Building blocks of the "settings page" and "drill-down" patterns of the design system
// (`.cursor/rules/frontend-design-system.mdc`): a tab that configures a feature, the pages
// reached from it, and the "Why?" explanation of an automated decision. They live apart from
// `ds.tsx` while that file is being reworked on the design system branch; once it is merged
// they belong there. Their props follow the reference components (`index.d.ts`).

function resolveIcon(name?: string | null) {
    if (!name) return null;
    return ((LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>>)[name] ?? null) as React.ComponentType<{
        className?: string;
        strokeWidth?: number;
    }> | null;
}

/* ------------------------------------------------------------------ */
/* Alert                                                                */
/* ------------------------------------------------------------------ */

export type InlineAlertType = "info" | "success" | "warning" | "error";

const ALERT_CLASSES: Record<InlineAlertType, { box: string; icon: string; Icon: React.ComponentType<{ className?: string }> }> = {
    info: { box: "bg-blue-50", icon: "text-blue-800", Icon: Info },
    success: { box: "bg-green-50", icon: "text-green-800", Icon: CircleCheck },
    warning: { box: "bg-amber-50", icon: "text-amber-800", Icon: AlertTriangle },
    error: { box: "bg-red-50", icon: "text-red-800", Icon: CircleAlert }
};

/**
 * Alert that sits inside a tab or a sheet: no panel around it (`AlertLayout` is the side panel of
 * a record). The tone sets the ground and the icon; the text stays in ink.
 */
export function InlineAlert({ type = "info", title, children, action, className }: { type?: InlineAlertType; title?: React.ReactNode; children?: React.ReactNode; action?: React.ReactNode; className?: string }) {
    const { box, icon, Icon } = ALERT_CLASSES[type];
    return (
        <div role={type === "error" ? "alert" : "status"} className={cn("flex w-full items-start gap-2 rounded-md p-3 text-sm text-gray-900", box, className)}>
            <Icon className={cn("mt-0.5 h-4 w-4 flex-shrink-0", icon)} />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {title && <span className="font-semibold">{title}</span>}
                {children && <div>{children}</div>}
            </div>
            {action && <div className="flex-shrink-0">{action}</div>}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Settings page                                                        */
/* ------------------------------------------------------------------ */

/** One block of a settings page: title and description on the left, controls on the right. Blocks are separated by a hairline, never boxed. */
export function SettingsSection({
    step,
    title,
    description,
    actions,
    stacked,
    children
}: {
    /** Numbered only when the order matters (first setup). */
    step?: number;
    /** Title above the controls instead of beside them: for a page in the half-width column of a record. */
    stacked?: boolean;
    title: React.ReactNode;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    children?: React.ReactNode;
}) {
    return (
        <section className={cn("grid grid-cols-1 gap-4 border-t py-6 first:border-t-0", !stacked && "md:grid-cols-[minmax(0,1fr)_minmax(0,2.6fr)] md:gap-8")}>
            <div className="flex flex-col items-start gap-2">
                <h3 className="flex items-center gap-2 text-base font-bold text-black">
                    {step !== undefined && <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-700">{step}</span>}
                    {title}
                </h3>
                {description && <p className="text-sm text-gray-600">{description}</p>}
                {actions}
            </div>
            <div className="flex min-w-0 flex-col gap-4">{children}</div>
        </section>
    );
}

/** How many pending changes the save bar spells out; the rest is counted. */
const SHOWN_CHANGES = 3;

/**
 * Sticky footer of a settings page: save state on the left, Discard and Save on the right (the
 * page's single Save). Everything the page edits is a local draft until Save, so the bar says what
 * is pending (`changes`: a short list, the first few shown) and, when nothing is, that nothing is —
 * never that "everything is saved", which would read as an autosave. As the closing bar of a
 * drill-down, pass `message` and `actions` instead.
 */
export function SaveBar({
    dirtyCount = 0,
    changes,
    saving,
    message,
    onDiscard,
    onSave,
    saveLabel = "Save",
    secondary,
    actions
}: {
    dirtyCount?: number;
    /** What the draft holds, one short label each: « Cap per pass », « Exclusion “VIP” removed »… */
    changes?: string[];
    saving?: boolean;
    message?: React.ReactNode;
    onDiscard?: () => void;
    /** Omit when the bar sits in a `<form>`: Save then submits it. */
    onSave?: () => void;
    saveLabel?: string;
    secondary?: React.ReactNode;
    actions?: React.ReactNode;
}) {
    const dirty = dirtyCount > 0;
    const shown = changes?.slice(0, SHOWN_CHANGES) ?? [];
    const hidden = (changes?.length ?? 0) - shown.length;
    return (
        <div className="sticky bottom-0 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 border-t bg-white py-3" data-testid="save-bar">
            <span className="flex flex-wrap items-center gap-x-2 text-sm text-gray-900" role="status">
                {message ?? (
                    <>
                        {dirty && <span className="h-2 w-2 rounded-full bg-denim-blue" aria-hidden />}
                        {dirty ? `${dirtyCount} unsaved change${dirtyCount > 1 ? "s" : ""}` : "No unsaved changes"}
                        {dirty && shown.length > 0 && (
                            <span className="text-gray-600" data-testid="save-bar-changes">
                                — {shown.join(" · ")}
                                {hidden > 0 && ` · and ${hidden} more`}
                            </span>
                        )}
                    </>
                )}
            </span>
            <div className="flex flex-wrap items-center gap-2">
                {actions ?? (
                    <>
                        {secondary}
                        <Button type="button" variant="outline" size="sm" disabled={!dirty || saving} onClick={onDiscard}>
                            Discard
                        </Button>
                        <Button type={onSave ? "button" : "submit"} size="sm" disabled={!dirty || saving} onClick={onSave} data-testid="save-config">
                            {saving && <Loader className="mr-1 h-4 w-4 animate-spin" />}
                            {saveLabel}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

/** Help most readers skip: one link-styled line that opens an explanation in place. */
export function Disclosure({ title, defaultOpen = false, children }: { title: string; defaultOpen?: boolean; children?: React.ReactNode }) {
    return (
        <Collapsible defaultOpen={defaultOpen} className="group">
            <CollapsibleTrigger className="flex items-start gap-1 text-left text-sm text-blue-700 hover:text-blue-900 hover:underline">
                <ChevronRight className="mt-0.5 h-4 w-4 flex-shrink-0 group-data-[state=open]:hidden" />
                <ChevronDown className="mt-0.5 hidden h-4 w-4 flex-shrink-0 group-data-[state=open]:block" />
                {title}
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 rounded-sm border bg-gray-50 p-2 text-sm text-gray-700">{children}</CollapsibleContent>
        </Collapsible>
    );
}

/** Filter pill with an optional count; a row of them filters a table or a sample. */
export function ToggleChip({ label, count, pressed, onClick, testId }: { label: React.ReactNode; count?: number; pressed?: boolean; onClick?: () => void; testId?: string }) {
    return (
        <button
            type="button"
            aria-pressed={pressed}
            onClick={onClick}
            data-testid={testId}
            className={cn(
                "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                pressed && "border-denim-blue bg-denim-blue/10 font-medium text-black"
            )}
        >
            {pressed && <Check className="h-3 w-3" />}
            {label}
            {count !== undefined && <span className="tabular-nums text-gray-600">{count}</span>}
        </button>
    );
}

/** One option of an `OptionList`: `disabled` shows it, with its `description` (the reason), not selectable. */
export type OptionListOption = { value: string; label: string; description?: string | null; meta?: string; disabled?: boolean; group?: string | null };

/**
 * Choice of one item among a few rich options (title, description, figure): radio semantics, the whole row
 * is the target. Consecutive options sharing a `group` are listed under that heading.
 */
export function OptionList({
    options,
    value,
    onChange,
    label,
    id,
    disabled = false,
    maxHeight = "min(24rem, 50vh)"
}: {
    options: OptionListOption[];
    value?: string | null;
    onChange?: (value: string) => void;
    label?: string;
    /** Id of the radiogroup (a form field's name, for its `<label htmlFor>`). */
    id?: string;
    /** Nothing selectable (a read-only form field). */
    disabled?: boolean;
    /** Height beyond which the list scrolls (any CSS length). It also shrinks with a flex parent. */
    maxHeight?: string;
}) {
    const selectedRef = React.useRef<HTMLButtonElement>(null);
    // keeps the chosen option in view on mount and when the selection changes
    // Braces: an effect returns its cleanup, and recent browsers make scrollIntoView return a promise.
    React.useEffect(() => {
        selectedRef.current?.scrollIntoView?.({ block: "nearest" });
    }, [value]);
    return (
        <ScrollArea className="min-h-0" style={{ maxHeight }} data-testid="option-list-scroll">
            <div role="radiogroup" aria-label={label} id={id} aria-disabled={disabled || undefined} className="flex flex-col gap-2 p-1">
                {options.map((option, index) => {
                    const selected = option.value === value;
                    const unavailable = disabled || Boolean(option.disabled);
                    const heading = option.group && option.group !== options[index - 1]?.group ? option.group : null;
                    return (
                        <React.Fragment key={option.value}>
                            {heading && <p className={cn("text-xs font-medium text-gray-500", index > 0 && "pt-1")}>{heading}</p>}
                            <button
                                ref={selected ? selectedRef : undefined}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                disabled={unavailable}
                                onClick={() => onChange?.(option.value)}
                                className={cn(
                                    "flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent",
                                    selected && "border-denim-blue bg-denim-blue/10 hover:bg-denim-blue/10"
                                )}
                            >
                                <span className={cn("flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border", selected ? "border-denim-blue" : "border-gray-400")} aria-hidden>
                                    {selected && <span className="h-2 w-2 rounded-full bg-denim-blue" />}
                                </span>
                                <span className="flex min-w-0 flex-1 flex-col">
                                    <span className="truncate text-black">{option.label}</span>
                                    {option.description && <span className="text-xs text-gray-500">{option.description}</span>}
                                </span>
                                {option.meta && <span className="flex-shrink-0 text-xs tabular-nums text-gray-500">{option.meta}</span>}
                            </button>
                        </React.Fragment>
                    );
                })}
            </div>
        </ScrollArea>
    );
}

/** Read-only filter chip: a field and its condition. */
export function FilterChip({ label, value }: { label: string; value?: string }) {
    return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
            {label}
            {value && <strong className="font-medium text-black">{value}</strong>}
        </span>
    );
}

/**
 * A ListView a feature depends on: its object, origin, filters, size and actions. One component
 * for the source of a campaign and for each exclusion.
 */
export function ListViewReference({
    name,
    object,
    badge,
    origin,
    description,
    filters,
    count,
    actions,
    onRemove,
    removeLabel
}: {
    name: string;
    /** Identity of the ListView's object, from `model_metadata`. */
    object?: { icon: string; color: string };
    badge?: string | { label: string; tone: BadgeTone };
    origin?: React.ReactNode;
    description?: React.ReactNode;
    filters?: Array<{ label: string; value?: string }>;
    count?: React.ReactNode;
    actions?: React.ReactNode;
    onRemove?: () => void;
    removeLabel?: string;
}) {
    const Icon = resolveIcon(object?.icon);
    const badgeValue = typeof badge === "string" ? { label: badge, tone: "neutral" as BadgeTone } : badge;
    return (
        <div className="flex w-full items-start gap-3 rounded-md border p-3">
            {object && (
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-sm p-1" style={{ backgroundColor: object.color }}>
                    {Icon && <Icon className="h-4 w-4 text-white" />}
                </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-black">{name}</span>
                    {badgeValue && <Badge tone={badgeValue.tone}>{badgeValue.label}</Badge>}
                </div>
                {origin && <span className="text-xs text-gray-600">{origin}</span>}
                {description && <span className="text-xs text-gray-600">{description}</span>}
                {filters && filters.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                        {filters.map((filter, index) => (
                            <FilterChip key={index} {...filter} />
                        ))}
                    </div>
                )}
            </div>
            <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
                {count !== undefined && <span className="text-xs tabular-nums text-gray-600">{count}</span>}
                {actions}
                {onRemove && (
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" title={removeLabel ?? `Remove ${name}`} aria-label={removeLabel ?? `Remove ${name}`} onClick={onRemove}>
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Drill-down                                                           */
/* ------------------------------------------------------------------ */

/** Title block of a drill-down page: title, badge, one meta line. */
export function DrillTitle({ title, badge, meta, actions }: { title: React.ReactNode; badge?: React.ReactNode; meta?: React.ReactNode; actions?: React.ReactNode }) {
    return (
        <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex min-w-0 flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-bold text-black">{title}</h2>
                    {badge}
                </div>
                {meta && <p className="text-xs text-gray-500">{meta}</p>}
            </div>
            {actions}
        </div>
    );
}

/** Reason of an outcome, as the backend serves it (`code`, `label`, `tone`, `count`, `description`, `temporary`). */
export type Reason = { code: string; label: string; tone: BadgeTone; count: number; description: string; temporary?: boolean };

/** Counts by reason with what each reason means: badge, count, a single-color bar against the total, the explanation. */
export function BreakdownList({ rows, total, onSelect }: { rows: Reason[]; total?: number; onSelect?: (code: string) => void }) {
    const sum = total ?? rows.reduce((acc, row) => acc + row.count, 0);
    return (
        <ul className="flex flex-col" data-testid="reason-breakdown">
            {rows.map(row => {
                const share = sum > 0 ? (row.count / sum) * 100 : 0;
                const badge = (
                    <Badge tone={row.tone} className="max-w-full truncate" title={row.label}>
                        {row.label}
                    </Badge>
                );
                return (
                    <li key={row.code} className="grid grid-cols-[minmax(0,12rem)_3rem_6rem_minmax(0,1fr)] items-center gap-3 border-b py-2 text-sm last:border-b-0">
                        {onSelect ? (
                            <button type="button" className="min-w-0 justify-self-start" aria-label={`Filter on ${row.label}`} onClick={() => onSelect(row.code)}>
                                {badge}
                            </button>
                        ) : (
                            <span className="min-w-0 justify-self-start">{badge}</span>
                        )}
                        <span className="text-right font-semibold tabular-nums text-black">{row.count.toLocaleString("fr-FR")}</span>
                        <span className="h-1.5 overflow-hidden rounded-full bg-gray-100" aria-hidden>
                            <span className="block h-full rounded-full bg-denim-blue" style={{ width: `${Math.max(share, 1.5)}%` }} />
                        </span>
                        <span className="text-xs text-gray-600">
                            {row.description}
                            {row.temporary && <span className="ml-1 text-gray-900">Temporary — retried at a later pass.</span>}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}

export type ChangeLogEntry = { title: string; author: string; date: string; changes: Array<{ what: string; before?: string | null; after?: string | null }> };

/** Audit trail of a configuration: one entry per save, expandable into a Before / After table. Read-only. */
export function ChangeLog({ entries, defaultOpen = 1 }: { entries: ChangeLogEntry[]; defaultOpen?: number }) {
    const [open, setOpen] = React.useState<Set<number>>(() => new Set(Array.from({ length: Math.min(defaultOpen, entries.length) }, (_, index) => index)));
    const toggle = (index: number) =>
        setOpen(previous => {
            const next = new Set(previous);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    return (
        <ol className="flex flex-col gap-2" data-testid="history-list">
            {entries.map((entry, index) => {
                const expanded = open.has(index);
                return (
                    <li key={index} className="rounded-md border">
                        <button type="button" className="flex w-full items-center gap-3 p-3 text-left" aria-expanded={expanded} onClick={() => toggle(index)} data-testid={`history-${index}`}>
                            {expanded ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-600" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-600" />}
                            <span className="flex min-w-0 flex-1 flex-col">
                                <span className="text-sm font-semibold text-black">{entry.title}</span>
                                <span className="text-xs text-gray-600">
                                    {entry.author} · {entry.date}
                                </span>
                            </span>
                            <Badge tone="neutral">
                                {entry.changes.length} change{entry.changes.length > 1 ? "s" : ""}
                            </Badge>
                        </button>
                        {expanded && (
                            <div className="px-3 pb-3">
                                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,2fr)] rounded-sm bg-gray-100 text-xs text-gray-600">
                                    <span className="px-2 py-1.5">What</span>
                                    <span className="px-2 py-1.5">Before</span>
                                    <span className="px-2 py-1.5">After</span>
                                </div>
                                {entry.changes.map((change, row) => (
                                    <div key={row} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,2fr)] border-b text-sm last:border-b-0">
                                        <span className="px-2 py-1.5 text-xs text-gray-600">{change.what}</span>
                                        <span className={cn("px-2 py-1.5 text-gray-900", change.before ? "bg-red-50 line-through" : "")}>
                                            {change.before ?? <span className="text-gray-400">—</span>}
                                        </span>
                                        <span className={cn("px-2 py-1.5 font-semibold text-gray-900", change.after ? "bg-green-50" : "")}>
                                            {change.after ?? <span className="font-normal text-gray-400">—</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

export type EventLogEvent = {
    time: string;
    /** A Lucide name, or the icon component itself when the caller already resolved it. */
    icon?: string | React.ComponentType<{ className?: string }>;
    title: React.ReactNode;
    /** Workflow node the event happened on. */
    tag?: { label: string };
    details?: Array<[string, React.ReactNode]>;
    /** Technical event, hidden behind a switch. */
    engine?: boolean;
    /** An event that ends the record abnormally. */
    tone?: "danger";
};
export type EventLogGroup = { label: string; events: EventLogEvent[] };

/** Journal of a record's life inside an engine (a dialer card): events grouped by day, each expandable into its facts, technical events behind a switch. */
export function EventLog({ groups, showEngineDefault = false, defaultOpen }: { groups: EventLogGroup[]; showEngineDefault?: boolean; defaultOpen?: string }) {
    const [showEngine, setShowEngine] = React.useState(showEngineDefault);
    const [open, setOpen] = React.useState<string | null>(defaultOpen ?? null);
    const hidden = groups.reduce((acc, group) => acc + group.events.filter(event => event.engine).length, 0);
    return (
        <div className="flex flex-col gap-3">
            {hidden > 0 && (
                <label className="flex w-fit cursor-pointer items-center gap-2 text-sm text-gray-900">
                    <button
                        type="button"
                        role="switch"
                        aria-checked={showEngine}
                        onClick={() => setShowEngine(value => !value)}
                        className={cn(
                            "relative h-5 w-9 flex-shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            showEngine ? "bg-denim-blue" : "bg-gray-400"
                        )}
                    >
                        <span className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all", showEngine ? "left-[18px]" : "left-0.5")} />
                    </button>
                    Show engine events
                    <span className="text-xs text-gray-500">{showEngine ? `${hidden} shown` : `${hidden} hidden`}</span>
                </label>
            )}
            {groups.map(group => {
                const events = group.events.filter(event => showEngine || !event.engine);
                if (events.length === 0) return null;
                return (
                    <div key={group.label} className="flex flex-col gap-1">
                        <div className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">{group.label}</div>
                        {events.map((event, index) => {
                            const key = `${group.label}:${index}`;
                            const expanded = open === key;
                            const Icon = typeof event.icon === "string" ? resolveIcon(event.icon) : event.icon;
                            const expandable = (event.details?.length ?? 0) > 0;
                            const danger = event.tone === "danger";
                            return (
                                <div key={key} className={cn("rounded-md border", danger && "border-red-200 bg-red-50")}>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-2 py-2 text-left text-sm"
                                        aria-expanded={expandable ? expanded : undefined}
                                        disabled={!expandable}
                                        onClick={() => setOpen(expanded ? null : key)}
                                    >
                                        <span className={cn("flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full", danger ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-600")}>
                                            {Icon && <Icon className="h-3.5 w-3.5" />}
                                        </span>
                                        <span className="w-12 flex-shrink-0 text-xs tabular-nums text-gray-600">{event.time}</span>
                                        <span className="min-w-0 flex-1 text-black">{event.title}</span>
                                        {event.tag && <Badge tone="neutral">{event.tag.label}</Badge>}
                                        {expandable && (expanded ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-600" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-600" />)}
                                    </button>
                                    {expanded && event.details && (
                                        <dl className="mx-2 mb-2 flex flex-col border-t pt-1">
                                            {event.details.map(([label, value]) => (
                                                <div key={label} className="grid grid-cols-[10rem_minmax(0,1fr)] gap-2 py-1 text-sm">
                                                    <dt className="text-xs text-gray-600">{label}</dt>
                                                    <dd className="min-w-0 text-black">{value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Explanation sheet ("Why?")                                           */
/* ------------------------------------------------------------------ */

/** "Why?" payload served by the backend for a card, a record or a preview row. */
export type Explanation = {
    title: string;
    body: string;
    tone: InlineAlertType;
    facts: Array<[string, string]>;
    actions: Array<{ label: string; href: string }>;
    reason: { code: string; label: string; tone: BadgeTone };
};

/** Side panel over the page for detail that must not lose the user's place. A form that changes data is a dialog, not a sheet. */
export function SidePanel({
    open,
    onOpenChange,
    title,
    description,
    children,
    footer
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: React.ReactNode;
    description?: React.ReactNode;
    children?: React.ReactNode;
    footer?: React.ReactNode;
}) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="flex w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[30rem]">
                <SheetHeader className="space-y-1 p-6 pb-3 pr-12">
                    <SheetTitle>{title}</SheetTitle>
                    {description && <SheetDescription className="text-sm text-gray-600">{description}</SheetDescription>}
                </SheetHeader>
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-3">{children}</div>
                {footer && <SheetFooter className="flex-wrap gap-2 border-t p-4 sm:space-x-0">{footer}</SheetFooter>}
            </SheetContent>
        </Sheet>
    );
}

/**
 * The one "Why?" of the CRM: an alert with the conclusion, the facts as a field grid, the reason
 * badge and links to go further. Opened from a table row, a card journal or a record panel.
 */
export function WhySheet({
    explanation,
    subject,
    open,
    onOpenChange,
    state
}: {
    explanation: Explanation | null;
    subject?: React.ReactNode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Shown while there is no explanation yet: loading, or the error and its Retry. */
    state?: React.ReactNode;
}) {
    return (
        <SidePanel
            open={open}
            onOpenChange={onOpenChange}
            title="Why?"
            description={subject}
            footer={explanation?.actions.map(action => (
                <Button key={action.label} variant="outline" size="sm" asChild>
                    <Link to={action.href} onClick={() => onOpenChange(false)}>
                        {action.label}
                    </Link>
                </Button>
            ))}
        >
            {!explanation && state}
            {explanation && <WhyBody explanation={explanation} />}
        </SidePanel>
    );
}

/** The content of a "Why?": the conclusion as an alert, the facts as a field grid, the reason badge. */
export function WhyBody({ explanation }: { explanation: Explanation }) {
    return (
        <div className="flex flex-col gap-4" data-testid="why-view">
            <InlineAlert type={explanation.tone} title={<span data-testid="why-headline">{explanation.title}</span>}>
                <span data-testid="why-sentence">{explanation.body}</span>
            </InlineAlert>
            {explanation.facts.length > 0 && (
                <dl className="flex flex-col">
                    {explanation.facts.map(([label, value]) => (
                        <div key={label} className="grid grid-cols-[8rem_minmax(0,1fr)] gap-2 border-b py-1.5 text-sm last:border-b-0">
                            <dt className="text-xs text-gray-600">{label}</dt>
                            <dd className="min-w-0 text-black">{value}</dd>
                        </div>
                    ))}
                </dl>
            )}
            <div className="flex items-center gap-2 text-xs text-gray-600">
                Reason code <Badge tone={explanation.reason.tone}>{explanation.reason.label}</Badge>
            </div>
        </div>
    );
}

/**
 * "Why?" inside a dialog: a pane on the right of the dialog's table instead of a sheet, since a
 * dialog never opens an overlay on top of itself. Same content as `WhySheet`.
 */
export function WhyPane({
    explanation,
    subject,
    onClose,
    state
}: {
    explanation: Explanation | null;
    subject?: React.ReactNode;
    onClose: () => void;
    /** Shown while there is no explanation yet: loading, or the error and its Retry. */
    state?: React.ReactNode;
}) {
    return (
        <aside aria-label="Why?" className="flex min-h-0 w-full flex-col border-t bg-gray-50 lg:w-80 lg:flex-shrink-0 lg:border-l lg:border-t-0" data-testid="why-pane">
            <div className="flex items-start justify-between gap-2 border-b px-4 py-3">
                <div className="flex min-w-0 flex-col">
                    <span className="text-sm font-semibold text-black">Why?</span>
                    {subject && <span className="truncate text-xs text-gray-600">{subject}</span>}
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={onClose} data-testid="why-close">
                    Close
                </Button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
                {explanation ? <WhyBody explanation={explanation} /> : state}
                {explanation && explanation.actions.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {explanation.actions.map(action => (
                            <Button key={action.label} variant="outline" size="sm" asChild>
                                <Link to={action.href}>{action.label}</Link>
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </aside>
    );
}

/** One row of a `FilterBars`: a share of a total, which filters a list when chosen. */
export type FilterBar = { code: string; label: string; count: number; tone?: BadgeTone; description?: string };

/**
 * The column on the left of a detail dialog: how a total breaks down (one row per reason, with a bar),
 * each row filtering the list next to it. "All" is first. Narrow on purpose (`BreakdownList` explains).
 */
export function FilterBars({ rows, total, value, onChange, allLabel = "All" }: { rows: FilterBar[]; total: number; value: string | null; onChange: (code: string | null) => void; allLabel?: string }) {
    const all = [{ code: "", label: allLabel, count: total, description: undefined }, ...rows];
    return (
        <div role="radiogroup" aria-label="Filter by reason" className="flex flex-col gap-1" data-testid="reason-breakdown">
            {all.map(row => {
                const selected = (value ?? "") === row.code;
                const share = total > 0 ? (row.count / total) * 100 : 0;
                return (
                    <button
                        key={row.code || "all"}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        title={row.description}
                        onClick={() => onChange(row.code || null)}
                        data-testid={`reason-filter-${row.code || "all"}`}
                        className={cn("flex flex-col gap-1 rounded-md border border-transparent px-2.5 py-2 text-left text-sm hover:bg-gray-50", selected && "border-denim-blue bg-denim-blue/10 hover:bg-denim-blue/10")}
                    >
                        <span className="flex items-center justify-between gap-2">
                            <span className="min-w-0 truncate text-black">{row.label}</span>
                            <span className="tabular-nums text-gray-600">{row.count.toLocaleString("fr-FR")}</span>
                        </span>
                        <span className="h-1 overflow-hidden rounded-full bg-gray-100" aria-hidden>
                            <span className="block h-full rounded-full bg-denim-blue" style={{ width: `${Math.max(share, 1.5)}%` }} />
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Object choice                                                        */
/* ------------------------------------------------------------------ */

export type ObjectChoiceItem = { value: string; label: string; icon?: string | null; color?: string | null; meta?: React.ReactNode };

/**
 * Choice of an object as a row of tiles (its identity icon and color from the metadata, its label, a short
 * figure): radio semantics. Used where the object frames what follows (the records of a campaign, an exclusion).
 */
export function ObjectChoice({ items, value, onChange, label }: { items: ObjectChoiceItem[]; value: string; onChange: (value: string) => void; label: string }) {
    return (
        <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="object-choice">
            {items.map(item => {
                const selected = item.value === value;
                const Icon = resolveIcon(item.icon);
                return (
                    <button
                        key={item.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => onChange(item.value)}
                        data-testid={`object-${item.value}`}
                        className={cn(
                            "flex min-w-0 items-center gap-2.5 rounded-md border bg-white px-2.5 py-2 text-left text-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            selected && "border-denim-blue bg-denim-blue/10 ring-1 ring-denim-blue hover:bg-denim-blue/10"
                        )}
                    >
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-sm" style={{ backgroundColor: item.color ?? undefined }} aria-hidden>
                            {Icon && <Icon className="h-4 w-4 text-white" />}
                        </span>
                        <span className="flex min-w-0 flex-col">
                            <span className={cn("truncate text-black", selected && "font-semibold")}>{item.label}</span>
                            {item.meta && <span className="truncate text-xs text-gray-500">{item.meta}</span>}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/** A small identity tile of an object: its icon, white on its color. */
export function ObjectBadgeTile({ icon, color, size = "sm" }: { icon?: string | null; color?: string | null; size?: "xs" | "sm" }) {
    const Icon = resolveIcon(icon);
    return (
        <span className={cn("inline-flex flex-shrink-0 items-center justify-center rounded-sm", size === "xs" ? "h-5 w-5" : "h-6 w-6")} style={{ backgroundColor: color ?? undefined }} aria-hidden>
            {Icon && <Icon className={cn("text-white", size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5")} />}
        </span>
    );
}

/* ------------------------------------------------------------------ */
/* Step list                                                            */
/* ------------------------------------------------------------------ */

export type StepStatus = "done" | "todo" | "next" | "optional";

export type StepListItem = {
    code: string;
    title: React.ReactNode;
    /** Shown only while the object is set up; once it is ready the rows carry no check. */
    status?: StepStatus;
    /** What the step holds now, in a line or two (chips, a count). */
    summary?: React.ReactNode;
    /** The one action that edits the step (opens its dialog). */
    action?: React.ReactNode;
};

const STEP_BADGE: Partial<Record<StepStatus, { label: string; tone: BadgeTone }>> = { next: { label: "Next step", tone: "info" }, optional: { label: "Optional", tone: "neutral" } };

/**
 * The configuration of a feature as the checklist of its steps: each row says what the step holds and
 * carries the action that edits it, so the checklist sits where the edits happen. Numbered while the
 * feature is set up (`status`), plain rows once it is ready.
 */
export function StepList({ items, footer }: { items: StepListItem[]; footer?: React.ReactNode }) {
    return (
        <ol className="flex flex-col">
            {items.map((item, index) => {
                const badge = item.status && STEP_BADGE[item.status];
                return (
                    <li key={item.code} className={cn("flex items-start gap-3 border-b px-2 py-2.5 text-sm", item.status === "next" && "bg-denim-blue/10")} data-testid={`step-${item.code}`}>
                        {item.status && (
                            <span
                                className={cn(
                                    "mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                                    item.status === "done" ? "bg-green-100 text-green-800" : item.status === "next" ? "border-2 border-denim-blue text-denim-blue" : "border border-gray-300 text-gray-600"
                                )}
                                aria-label={item.status === "done" ? "Done" : undefined}
                            >
                                {item.status === "done" ? <Check className="h-3 w-3" /> : index + 1}
                            </span>
                        )}
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="flex flex-wrap items-center gap-2 font-semibold text-black">
                                {item.title}
                                {badge && <Badge tone={badge.tone}>{badge.label}</Badge>}
                            </span>
                            {item.summary && <div className="text-gray-600">{item.summary}</div>}
                        </div>
                        {item.action && <div className="flex flex-shrink-0 items-center gap-2">{item.action}</div>}
                    </li>
                );
            })}
            {footer && <li className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-2 py-2.5 text-sm">{footer}</li>}
        </ol>
    );
}

/* ------------------------------------------------------------------ */
/* Dense table                                                          */
/* ------------------------------------------------------------------ */

/** Rows that are not backend records, in the standard dense table: no selection, no resizing, no ordering. */
export function DenseTable<T>({ rows, columns, itemToId, primaryColumn }: { rows: T[]; columns: ObjectListColumn<T>[]; itemToId: (row: T) => string; primaryColumn?: string }) {
    return (
        <ObjectListTableResizable<T>
            columns={columns}
            paginated_response={{ count: rows.length, results: rows }}
            allowAutoRedirect={false}
            allowLateralScroll={false}
            allowBatchSelection={false}
            allowColumnResizing={false}
            allowColumnOrdering={false}
            layout="dense"
            primary_column={primaryColumn}
            itemToId={itemToId}
            onRowNavigateTo={() => null}
        />
    );
}
